/**
 * <es-slideshow> - a hero slideshow built on a native scroll-snap track.
 *
 * WHY SCROLL-SNAP. Swipe, trackpad, wheel and keyboard scrolling all come free from the browser, and Arabic
 * (RTL) works because the browser lays the track out in the writing direction. The alternative - hand-rolled
 * pointer gestures over stacked, faded slides - is far more code and is where slideshows usually break. Without
 * this script the slides are still a fully scrollable strip; the script adds arrows, dots, autoplay and state.
 *
 * AUTOPLAY is opt-in and deliberately polite. It pauses whenever the user could be reading or interacting:
 *   pointer over it, keyboard focus inside it, finger on it, tab hidden, scrolled out of view, the user pressed
 *   Pause, or the Theme Editor has a block selected. It never runs at all under prefers-reduced-motion. When it
 *   runs, a Pause button is always present (WCAG 2.2.2).
 *
 * Off-screen slides are `inert`, so keyboard users cannot Tab into content they cannot see.
 *
 * Markup contract (rendered by sections/slideshow.liquid):
 *   <es-slideshow data-autoplay="true|false" data-delay="5">
 *     <div data-slideshow-track> <div data-slideshow-slide>...</div> ... </div>
 *     <button data-slideshow-prev> <button data-slideshow-next>
 *     <button data-slideshow-dot="0"> ...
 *     <button data-slideshow-toggle data-label-pause="..." data-label-play="...">
 *   </es-slideshow>
 */
class ESSlideshow extends HTMLElement {
  constructor() {
    super();

    this.onScroll = this.onScroll.bind(this);
    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onDotClick = this.onDotClick.bind(this);
    this.onToggleClick = this.onToggleClick.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onPointerEnter = this.onPointerEnter.bind(this);
    this.onPointerLeave = this.onPointerLeave.bind(this);
    this.onFocusIn = this.onFocusIn.bind(this);
    this.onFocusOut = this.onFocusOut.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.onVisibilityChange = this.onVisibilityChange.bind(this);
    this.onIntersect = this.onIntersect.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);
    this.onBlockDeselect = this.onBlockDeselect.bind(this);
    this.syncHeight = this.syncHeight.bind(this);

    this.track = null;
    this.slides = [];
    this.dots = [];
    this.prevButton = null;
    this.nextButton = null;
    this.toggleButton = null;
    this.intersectionObserver = null;
    this.heightObserver = null;
    this.observedSlide = null;
    this.reducedMotion = null;

    this.current = 0;
    this.timer = null;
    this.frame = null;

    /* Independent reasons autoplay may be held back. Autoplay runs only when every one of them is false. */
    this.hold = {
      user: false,
      pointer: false,
      focus: false,
      touch: false,
      hidden: false,
      offscreen: false,
      editor: false,
    };
  }

  connectedCallback() {
    this.track = this.querySelector('[data-slideshow-track]');
    this.slides = Array.from(this.querySelectorAll('[data-slideshow-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-slideshow-dot]'));
    this.prevButton = this.querySelector('[data-slideshow-prev]');
    this.nextButton = this.querySelector('[data-slideshow-next]');
    this.toggleButton = this.querySelector('[data-slideshow-toggle]');

    if (!this.track || this.slides.length === 0) return;

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.delay = (parseInt(this.dataset.delay, 10) || 5) * 1000;
    this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1 && !this.reducedMotion.matches;

    /* Autoplay is off (reduced motion, or nothing to rotate to): a Pause button would control nothing. */
    if (this.toggleButton && !this.autoplay) this.toggleButton.hidden = true;

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    this.prevButton?.addEventListener('click', this.onPrevClick);
    this.nextButton?.addEventListener('click', this.onNextClick);
    this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
    this.toggleButton?.addEventListener('click', this.onToggleClick);

    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('mouseenter', this.onPointerEnter);
    this.addEventListener('mouseleave', this.onPointerLeave);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.addEventListener('touchend', this.onTouchEnd, { passive: true });
    this.addEventListener('touchcancel', this.onTouchEnd, { passive: true });
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    /* Theme Editor: selecting a slide in the sidebar reveals it and holds autoplay so the merchant can edit. */
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this.addEventListener('shopify:block:deselect', this.onBlockDeselect);

    /* Height "Adapt to image": the viewport follows the active slide's image height. A ResizeObserver on the active
       slide catches image load and width changes. It observes the SLIDE, whose height never depends on the track,
       so setting the track height cannot feed back into another observation. */
    if (this.dataset.adaptHeight === 'true' && typeof ResizeObserver !== 'undefined') {
      this.heightObserver = new ResizeObserver(this.syncHeight);
    }

    if (typeof IntersectionObserver !== 'undefined') {
      this.intersectionObserver = new IntersectionObserver(this.onIntersect, { threshold: 0.25 });
      this.intersectionObserver.observe(this);
    }

    this.setActive(this.indexFromScroll());
    this.refreshAutoplay();
  }

  disconnectedCallback() {
    this.stopTimer();

    if (this.frame !== null) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    this.track?.removeEventListener('scroll', this.onScroll);
    this.prevButton?.removeEventListener('click', this.onPrevClick);
    this.nextButton?.removeEventListener('click', this.onNextClick);
    this.dots.forEach((dot) => dot.removeEventListener('click', this.onDotClick));
    this.toggleButton?.removeEventListener('click', this.onToggleClick);

    this.removeEventListener('keydown', this.onKeyDown);
    this.removeEventListener('mouseenter', this.onPointerEnter);
    this.removeEventListener('mouseleave', this.onPointerLeave);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('touchstart', this.onTouchStart);
    this.removeEventListener('touchend', this.onTouchEnd);
    this.removeEventListener('touchcancel', this.onTouchEnd);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);

    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.removeEventListener('shopify:block:deselect', this.onBlockDeselect);

    this.intersectionObserver?.disconnect();
    this.intersectionObserver = null;

    this.heightObserver?.disconnect();
    this.heightObserver = null;
    this.observedSlide = null;
    if (this.track) this.track.style.height = '';

    this.track = null;
    this.slides = [];
    this.dots = [];
    this.prevButton = null;
    this.nextButton = null;
    this.toggleButton = null;
  }

  get isRtl() {
    return this.track ? getComputedStyle(this.track).direction === 'rtl' : false;
  }

  get scrollBehavior() {
    return this.reducedMotion?.matches ? 'auto' : 'smooth';
  }

  /* ---------------------------------------------------------------- position */

  indexFromScroll() {
    const width = this.track.clientWidth;
    if (!width) return this.current;

    const index = Math.round(Math.abs(this.track.scrollLeft) / width);
    return Math.max(0, Math.min(index, this.slides.length - 1));
  }

  /* Wraps at both ends so the arrows are never dead. */
  goTo(index, behavior = this.scrollBehavior) {
    if (!this.track || this.slides.length === 0) return;

    const count = this.slides.length;
    const target = ((index % count) + count) % count;
    const direction = this.isRtl ? -1 : 1;

    this.track.scrollTo({ left: direction * target * this.track.clientWidth, behavior });
    /* Reflect the target immediately rather than waiting for the smooth scroll to cross the halfway point. */
    this.setActive(target);
  }

  setActive(index) {
    this.current = index;

    this.slides.forEach((slide, i) => {
      slide.toggleAttribute('inert', i !== index);
    });

    this.dots.forEach((dot, i) => {
      if (i === index) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    this.observeActiveSlide();
  }

  observeActiveSlide() {
    if (!this.heightObserver) return;

    const slide = this.slides[this.current];
    if (!slide || slide === this.observedSlide) return;

    if (this.observedSlide) this.heightObserver.unobserve(this.observedSlide);
    this.observedSlide = slide;
    /* observe() reports the current size immediately, which sets the initial height. */
    this.heightObserver.observe(slide);
  }

  syncHeight() {
    if (!this.track || !this.observedSlide) return;

    const height = this.observedSlide.offsetHeight;
    if (height > 0) this.track.style.height = `${height}px`;
  }

  onScroll() {
    if (this.frame !== null) return;

    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      if (!this.track) return;

      const index = this.indexFromScroll();
      if (index !== this.current) this.setActive(index);
    });
  }

  /* ---------------------------------------------------------------- controls */

  onPrevClick() {
    this.goTo(this.current - 1);
    this.refreshAutoplay();
  }

  onNextClick() {
    this.goTo(this.current + 1);
    this.refreshAutoplay();
  }

  onDotClick(event) {
    const index = parseInt(event.currentTarget.dataset.slideshowDot, 10);
    if (Number.isNaN(index)) return;

    this.goTo(index);
    this.refreshAutoplay();
  }

  onToggleClick() {
    this.hold.user = !this.hold.user;
    this.refreshAutoplay();
  }

  /* Arrow keys move between slides. "Next" follows the writing direction, so Left is next in Arabic. */
  onKeyDown(event) {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;

    const tag = event.target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

    const forward = this.isRtl ? 'ArrowLeft' : 'ArrowRight';
    const backward = this.isRtl ? 'ArrowRight' : 'ArrowLeft';

    if (event.key === forward) {
      event.preventDefault();
      this.goTo(this.current + 1);
      this.refreshAutoplay();
    } else if (event.key === backward) {
      event.preventDefault();
      this.goTo(this.current - 1);
      this.refreshAutoplay();
    }
  }

  /* ---------------------------------------------------------------- autoplay */

  get shouldPlay() {
    if (!this.autoplay) return false;
    return !Object.values(this.hold).some(Boolean);
  }

  onPointerEnter() {
    this.hold.pointer = true;
    this.refreshAutoplay();
  }

  onPointerLeave() {
    this.hold.pointer = false;
    this.refreshAutoplay();
  }

  onFocusIn() {
    this.hold.focus = true;
    this.refreshAutoplay();
  }

  onFocusOut(event) {
    /* Focus moving between two controls inside the slideshow is still "focus inside". */
    if (event.relatedTarget && this.contains(event.relatedTarget)) return;

    this.hold.focus = false;
    this.refreshAutoplay();
  }

  onTouchStart() {
    this.hold.touch = true;
    this.refreshAutoplay();
  }

  onTouchEnd() {
    this.hold.touch = false;
    this.refreshAutoplay();
  }

  onVisibilityChange() {
    this.hold.hidden = document.hidden;
    this.refreshAutoplay();
  }

  onIntersect(entries) {
    const entry = entries[entries.length - 1];
    this.hold.offscreen = !entry.isIntersecting;
    this.refreshAutoplay();
  }

  stopTimer() {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  /* Single place that reconciles the timer, the live region and the Pause button with the current state. */
  refreshAutoplay() {
    this.stopTimer();
    if (!this.track) return;

    const playing = this.shouldPlay;

    /* A moving region must not announce every change; a stopped one should, so screen-reader users hear
       where a manual move landed. */
    this.track.setAttribute('aria-live', playing ? 'off' : 'polite');

    if (this.toggleButton) {
      const paused = !playing;
      const label = paused ? this.toggleButton.dataset.labelPlay : this.toggleButton.dataset.labelPause;
      if (label) this.toggleButton.setAttribute('aria-label', label);
      this.toggleButton.dataset.playing = paused ? 'false' : 'true';
    }

    if (playing) this.schedule();
  }

  schedule() {
    this.timer = setTimeout(() => {
      this.timer = null;
      this.goTo(this.current + 1);
      this.refreshAutoplay();
    }, this.delay);
  }

  /* ---------------------------------------------------------------- Theme Editor */

  onBlockSelect(event) {
    const slide = event.target.closest?.('[data-slideshow-slide]');
    const index = slide ? this.slides.indexOf(slide) : -1;

    this.hold.editor = true;
    if (index >= 0) this.goTo(index, 'auto');
    this.refreshAutoplay();
  }

  onBlockDeselect() {
    this.hold.editor = false;
    this.refreshAutoplay();
  }
}

if (!customElements.get('es-slideshow')) {
  customElements.define('es-slideshow', ESSlideshow);
}
